/**
 * useExpensesHandlers — Vague 1 refactor CaissePage (Feb 2026)
 *
 * Extrait les 8 handlers Expenses de CaissePage.jsx (≈380 lignes) dans un hook
 * dédié, avec signatures et comportements strictement identiques.
 *
 * Contient :
 *   • allocateExpenseToAccount(expense, accountId, affectsCA)
 *   • createExpense()
 *   • addCommonItem() / removeCommonItem(id) / getCommonTotal()
 *   • openReviseModal(expense) / addReviseItem() / removeReviseItem(_k)
 *     updateReviseItem(_k, patch) / getReviseTotal() / submitRevision(directApprove)
 *   • updateExpense(expenseId, updateData)
 *   • receiveExpenseStock(expenseOrId)
 *   • deleteExpense(expenseId)
 *   • convertExpenseToPO(expense)
 *   • assignExpenseToWeek(expenseId, weekStart)
 */
import { useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const formatPrice = (n) => Math.round(n || 0).toLocaleString("fr-FR");

export function useExpensesHandlers(ctx) {
  const {
    currentUser,
    availableAccounts,
    // Expense form + related state
    expenseForm, setExpenseForm,
    expenseSubmitLoading, setExpenseSubmitLoading,
    setShowExpenseModal,
    // Common items (multi-item Achats communs)
    commonItems, setCommonItems,
    commonNewItem, setCommonNewItem,
    // Revise modal
    revisingExpense, setRevisingExpense,
    reviseItems, setReviseItems,
    reviseSupplier, setReviseSupplier,
    setReviseNote,
    reviseNewItem, setReviseNewItem,
    setShowReviseModal,
    // Edit modal (legacy single-expense)
    setEditingExpense,
    // Data refetchers
    fetchExpenses,
    fetchAvailableAccounts,
    // Utility injected from parent (uses parent's currentUser)
    actorQs,
  } = ctx;

  // ────────────────────────────────────────────────
  // 1) Allocation compte courant (avec fallback smart : recharge / créer / négatif)
  // ────────────────────────────────────────────────
  const allocateExpenseToAccount = useCallback(async (expense, accountId, affectsCA = true) => {
    try {
      // Special sentinel : créer un nouveau compte dédié directement
      if (accountId === "__create_new__") {
        const expAmount = parseFloat(expense.amount) || 0;
        const ok = window.confirm(
          `Créer un NOUVEAU compte courant dédié de ${formatPrice(expAmount)} F pour cet achat ?\n\n` +
          `Nom : "Recharge auto pour ${(expense.description || '').slice(0, 60)}"\n` +
          `Solde initial : ${formatPrice(expAmount)} F`
        );
        if (!ok) return;
        const res = await axios.post(`${API}/expenses/${expense.id}/allocate-account-smart`, {
          mode: "create_new",
          new_account_name: `Recharge auto pour ${(expense.description || '').slice(0, 80)}`,
          affects_ca: affectsCA,
        });
        if (res.data?.success) {
          toast.success(`Nouveau compte créé (${formatPrice(expAmount)} F) et dépense imputée.`);
        }
        fetchExpenses();
        fetchAvailableAccounts();
        return;
      }
      if (!accountId) {
        await axios.delete(`${API}/expenses/${expense.id}/allocate-account`);
        toast.success("Compte courant détaché");
        fetchExpenses();
        fetchAvailableAccounts();
        return;
      }
      const acc = availableAccounts.find((a) => a.id === accountId);
      const expAmount = parseFloat(expense.amount) || 0;
      const balance = parseFloat(acc?.balance_available) || 0;
      // Si solde suffisant, allocation standard
      if (balance >= expAmount) {
        await axios.post(`${API}/expenses/${expense.id}/allocate-account`, {
          account_id: accountId,
          account_name: acc?.name || "",
          affects_ca: affectsCA,
        });
        toast.success(`Dépense imputée sur : ${acc?.name || ""}`);
        fetchExpenses();
        fetchAvailableAccounts();
        return;
      }
      // Solde insuffisant : demander la stratégie
      const missing = expAmount - balance;
      const choice = window.prompt(
        `⚠ Le compte "${acc?.name || ''}" n'a que ${formatPrice(balance)} F disponible.\n` +
        `Il manque ${formatPrice(missing)} F pour couvrir cet achat de ${formatPrice(expAmount)} F.\n\n` +
        `Choisissez votre stratégie en tapant le numéro :\n` +
        `1 — Recharger ce compte de ${formatPrice(missing)} F automatiquement\n` +
        `2 — Créer un NOUVEAU compte courant dédié de ${formatPrice(expAmount)} F\n` +
        `3 — Imputer quand même (le compte ira en négatif)\n` +
        `(annuler pour ne rien faire)`,
        "1"
      );
      if (!choice) return;
      let mode = null;
      if (choice.trim() === "1") mode = "topup_existing";
      else if (choice.trim() === "2") mode = "create_new";
      else if (choice.trim() === "3") mode = "allow_negative";
      else {
        toast.error("Choix invalide.");
        return;
      }
      const body = { mode, affects_ca: affectsCA };
      if (mode === "create_new") {
        body.new_account_name = `Recharge auto pour ${(expense.description || '').slice(0, 80)}`;
      } else {
        body.account_id = accountId;
      }
      const res = await axios.post(`${API}/expenses/${expense.id}/allocate-account-smart`, body);
      const topUp = res.data?.topped_up_amount || 0;
      if (mode === "topup_existing") toast.success(`Compte rechargé de ${formatPrice(topUp)} F et dépense imputée.`);
      else if (mode === "create_new") toast.success(`Nouveau compte créé (${formatPrice(expAmount)} F) et dépense imputée.`);
      else toast.success(`Dépense imputée — le compte est en découvert.`);
      fetchExpenses();
      fetchAvailableAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'imputation");
    }
  }, [availableAccounts, fetchExpenses, fetchAvailableAccounts]);

  // ────────────────────────────────────────────────
  // 2) Création d'un achat (single or multi-items)
  // ────────────────────────────────────────────────
  const createExpense = useCallback(async () => {
    if (expenseSubmitLoading) return;
    // Régularisation : validations
    if (expenseForm.is_regularization) {
      if (!expenseForm.posting_date) { toast.error("Date rétroactive requise pour une régularisation"); return; }
      const today = new Date().toISOString().slice(0, 10);
      if (expenseForm.posting_date > today) { toast.error("La date rétroactive ne peut pas être postérieure à aujourd'hui"); return; }
      if (!expenseForm.regularization_reason || !expenseForm.regularization_reason.trim()) {
        toast.error("Motif de régularisation obligatoire"); return;
      }
    }
    try {
      setExpenseSubmitLoading(true);
      // Multi-items mode
      if (commonItems.length > 0) {
        const totalAmount = commonItems.reduce((s, it) => s + (it.quantity || 1) * (it.unit_price || 0), 0);
        const firstCat = commonItems[0]?.category || expenseForm.category || "autres";
        const desc = expenseForm.description?.trim()
          || `Achats communs - ${commonItems.length} article(s) - ${format(new Date(), "dd/MM/yyyy")}`;
        await axios.post(`${API}/expenses?${actorQs()}`, {
          category: firstCat,
          description: desc,
          amount: totalAmount,
          quantity: commonItems.length,
          unit_price: null,
          supplier: expenseForm.supplier || null,
          planned_date: expenseForm.planned_date,
          receipt_image: expenseForm.receipt_image,
          is_group: true,
          items: commonItems.map((it) => ({
            category: it.category || firstCat,
            description: it.description,
            quantity: it.quantity || 1,
            unit_price: it.unit_price || 0,
            amount: (it.quantity || 1) * (it.unit_price || 0),
            expense_type: it.expense_type || expenseForm.expense_type || "achat",
            destination: it.destination || expenseForm.destination || null,
            passer_en_stock: (it.passer_en_stock === null || it.passer_en_stock === undefined) ? null : !!it.passer_en_stock,
          })),
          requested_by: currentUser?.full_name || currentUser?.username || "Responsable Op. & Log",
          funded_by_account_id: expenseForm.funded_by_account_id || null,
          funded_by_account_name: expenseForm.funded_by_account_name || null,
          funded_affects_ca: expenseForm.funded_affects_ca,
          expense_type: expenseForm.expense_type || "achat",
          destination: expenseForm.destination || null,
          to_stock: !!expenseForm.to_stock,
          is_regularization: !!expenseForm.is_regularization,
          posting_date: expenseForm.is_regularization ? expenseForm.posting_date : null,
          regularization_reason: expenseForm.is_regularization ? (expenseForm.regularization_reason || "").trim() : null,
        });
        toast.success(
          currentUser?.role === 'admin'
            ? `Achat direct validé automatiquement (${commonItems.length} article${commonItems.length > 1 ? 's' : ''}) ✅`
            : `Achats communs créés avec ${commonItems.length} article(s) !`
        );
        setShowExpenseModal(false);
        setCommonItems([]);
        setCommonNewItem({ category: "cuisine", description: "", quantity: 0, unit_price: 0, passer_en_stock: null });
        setExpenseForm({
          category: "cuisine", description: "", quantity: 1, unit_price: 0, amount: 0,
          supplier: "", planned_date: format(new Date(), "yyyy-MM-dd"), receipt_image: null,
        });
        fetchExpenses();
        return;
      }
      // Legacy single-item fallback
      if (!expenseForm.description || expenseForm.unit_price <= 0 || expenseForm.quantity <= 0) {
        toast.error("Ajoutez au moins un article dans la liste");
        return;
      }
      const totalAmount = expenseForm.quantity * expenseForm.unit_price;
      const isAdmin = currentUser?.role === 'admin';
      await axios.post(`${API}/expenses?${actorQs()}`, {
        ...expenseForm,
        amount: totalAmount,
        requested_by: currentUser?.full_name || currentUser?.username || "Responsable Op. & Log"
      });
      toast.success(isAdmin ? "Achat direct enregistré et validé automatiquement ✅" : "Demande d'achat créée !");
      setShowExpenseModal(false);
      setExpenseForm({
        category: "cuisine", description: "", quantity: 1, unit_price: 0, amount: 0,
        supplier: "", planned_date: format(new Date(), "yyyy-MM-dd"), receipt_image: null,
      });
      fetchExpenses();
    } catch (error) {
      console.error("Error creating expense:", error);
      toast.error("Erreur lors de la création");
    } finally {
      setExpenseSubmitLoading(false);
    }
  }, [expenseSubmitLoading, expenseForm, commonItems, currentUser, actorQs, fetchExpenses,
      setExpenseSubmitLoading, setShowExpenseModal, setCommonItems, setCommonNewItem, setExpenseForm]);

  // ────────────────────────────────────────────────
  // 3) Common items helpers (multi-item Achats communs)
  // ────────────────────────────────────────────────
  const addCommonItem = useCallback(() => {
    if (!commonNewItem.description.trim()) { toast.error("Description requise"); return; }
    if ((commonNewItem.unit_price || 0) <= 0) { toast.error("Prix unitaire requis"); return; }
    const amount = (commonNewItem.quantity || 1) * (commonNewItem.unit_price || 0);
    setCommonItems([...commonItems, { ...commonNewItem, amount, id: Date.now() }]);
    setCommonNewItem({
      category: commonNewItem.category,
      description: "", quantity: 0, unit_price: 0,
      expense_type: commonNewItem.expense_type,
      destination: commonNewItem.destination,
    });
  }, [commonNewItem, commonItems, setCommonItems, setCommonNewItem]);

  const removeCommonItem = useCallback((id) => {
    setCommonItems(commonItems.filter((i) => i.id !== id));
  }, [commonItems, setCommonItems]);

  const getCommonTotal = useCallback(() => {
    return commonItems.reduce((s, it) => s + (it.amount || 0), 0);
  }, [commonItems]);

  // ────────────────────────────────────────────────
  // 4) Revise modal (Admin renvoie/approuve après modification)
  // ────────────────────────────────────────────────
  const openReviseModal = useCallback((expense) => {
    setRevisingExpense(expense);
    const existingItems = (expense.items && expense.items.length > 0)
      ? expense.items.map((it, i) => ({
          ...it,
          amount: (it.quantity || 1) * (it.unit_price || 0),
          _k: i + Date.now(),
        }))
      : [{
          category: expense.category || "cuisine",
          description: expense.description || "",
          quantity: expense.quantity || 1,
          unit_price: expense.unit_price || 0,
          amount: (expense.quantity || 1) * (expense.unit_price || 0),
          _k: Date.now(),
        }];
    setReviseItems(existingItems);
    setReviseSupplier(expense.supplier || "");
    setReviseNote("");
    setReviseNewItem({ category: "cuisine", description: "", quantity: 1, unit_price: 0 });
    setShowReviseModal(true);
  }, [setRevisingExpense, setReviseItems, setReviseSupplier, setReviseNote, setReviseNewItem, setShowReviseModal]);

  const addReviseItem = useCallback(() => {
    if (!reviseNewItem.description.trim()) { toast.error("Description requise"); return; }
    const amt = (reviseNewItem.quantity || 1) * (reviseNewItem.unit_price || 0);
    setReviseItems([...reviseItems, { ...reviseNewItem, amount: amt, _k: Date.now() }]);
    setReviseNewItem({ category: reviseNewItem.category, description: "", quantity: 1, unit_price: 0 });
  }, [reviseNewItem, reviseItems, setReviseItems, setReviseNewItem]);

  const removeReviseItem = useCallback((_k) => {
    setReviseItems(reviseItems.filter((i) => i._k !== _k));
  }, [reviseItems, setReviseItems]);

  const updateReviseItem = useCallback((_k, patch) => {
    setReviseItems(reviseItems.map((it) => {
      if (it._k !== _k) return it;
      const merged = { ...it, ...patch };
      merged.amount = (merged.quantity || 1) * (merged.unit_price || 0);
      return merged;
    }));
  }, [reviseItems, setReviseItems]);

  const getReviseTotal = useCallback(() => {
    return reviseItems.reduce((s, it) => s + (it.amount || 0), 0);
  }, [reviseItems]);

  const submitRevision = useCallback(async (directApprove = false) => {
    if (reviseItems.length === 0) { toast.error("Au moins un article requis"); return; }
    const total = reviseItems.reduce((s, it) => s + (it.amount || 0), 0);
    const payload = {
      status: directApprove ? "approved" : "revision_requested",
      admin_notes: ctx.reviseNote || (directApprove ? "Approuvé après modification" : "Veuillez réviser cette demande"),
      amount: total,
      supplier: reviseSupplier || null,
      items: reviseItems.map((it) => ({
        category: it.category,
        description: it.description,
        quantity: it.quantity || 1,
        unit_price: it.unit_price || 0,
        amount: it.amount || (it.quantity || 1) * (it.unit_price || 0),
      })),
    };
    if (directApprove) payload.approved_by = "Administrateur";
    try {
      await axios.put(`${API}/expenses/${revisingExpense.id}`, payload);
      toast.success(directApprove ? "Demande approuvée après modification" : "Demande renvoyée à la responsable op. & log pour révision");
      setShowReviseModal(false);
      setRevisingExpense(null);
      fetchExpenses();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  }, [reviseItems, reviseSupplier, revisingExpense, ctx.reviseNote, fetchExpenses,
      setShowReviseModal, setRevisingExpense]);

  // ────────────────────────────────────────────────
  // 5) Mise à jour / réception stock / suppression / conversion / rattachement
  // ────────────────────────────────────────────────
  const updateExpense = useCallback(async (expenseId, updateData) => {
    try {
      await axios.put(`${API}/expenses/${expenseId}`, updateData);
      toast.success("Dépense mise à jour !");
      fetchExpenses();
      setEditingExpense(null);
    } catch (error) {
      console.error("Error updating expense:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  }, [fetchExpenses, setEditingExpense]);

  const receiveExpenseStock = useCallback(async (expenseOrId) => {
    const expenseId = typeof expenseOrId === "string" ? expenseOrId : expenseOrId?.id;
    if (!expenseId) { toast.error("Identifiant d'achat manquant"); return; }
    try {
      const res = await axios.post(`${API}/expenses/${expenseId}/receive-stock`, {
        user_name: currentUser?.full_name || currentUser?.username || "Caisse",
      });
      if (res.data?.already_received) {
        toast.info("Cet achat est déjà en stock");
      } else {
        toast.success(`${res.data?.received_items || 0} article(s) ajouté(s) au stock`);
      }
      fetchExpenses();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de réception");
    }
  }, [currentUser, fetchExpenses]);

  const deleteExpense = useCallback(async (expenseId) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm("Supprimer cette dépense ?")) return;
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      toast.success("Dépense supprimée");
      fetchExpenses();
    } catch (error) {
      console.error("Error deleting expense:", error);
      toast.error("Erreur lors de la suppression");
    }
  }, [fetchExpenses]);

  const convertExpenseToPO = useCallback(async (expense) => {
    if (expense.converted_to_po_id) {
      toast.info(`Déjà converti en ${expense.converted_to_po_number || "BC"}`);
      return;
    }
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm(`Convertir « ${expense.description} » en bon de commande ?`)) return;
    try {
      const res = await axios.post(`${API}/purchase-orders/from-expense/${expense.id}`, {
        created_by: currentUser?.name || currentUser?.username || "Admin",
      });
      if (res.data?.success) {
        toast.success(`Bon de commande créé : ${res.data.purchase_order?.number}`);
        fetchExpenses();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la conversion");
    }
  }, [currentUser, fetchExpenses]);

  const assignExpenseToWeek = useCallback(async (expenseId, weekStart) => {
    try {
      await axios.put(`${API}/expenses/${expenseId}/assign-week`, { week_start: weekStart });
      toast.success("Dépense rattachée à la semaine");
      fetchExpenses();
    } catch (error) {
      console.error("Error assigning expense to week:", error);
      toast.error("Erreur lors du rattachement");
    }
  }, [fetchExpenses]);

  return {
    // Allocation compte courant
    allocateExpenseToAccount,
    // Création
    createExpense,
    // Multi-items helpers
    addCommonItem,
    removeCommonItem,
    getCommonTotal,
    // Revise modal
    openReviseModal,
    addReviseItem,
    removeReviseItem,
    updateReviseItem,
    getReviseTotal,
    submitRevision,
    // Mutations
    updateExpense,
    receiveExpenseStock,
    deleteExpense,
    convertExpenseToPO,
    assignExpenseToWeek,
  };
}
